(function () {
  'use strict';

  var API_BASE = 'https://api.geostock.app/shop/unimarketyyc.myshopify.com/variant_locations/';
  var requestIds = new WeakMap();
  var DEBUG = /[?&]ibl-debug=true/.test(window.location.search);

  var SOFT_FALLBACK_TEXT = 'Stock will be confirmed at checkout.';
  var NO_LOCAL_STOCK_TEXT = 'No stock at nearest location';
  var CACHE_PREFIX = 'ibl:';
  var CACHE_TTL_MS = 5 * 60 * 1000;
  var RETRY_DELAY_MS = 800;
  var REQUEST_TIMEOUT_MS = 2500;
  var MAX_REQUEST_ATTEMPTS = 3;

  function readCache(variantId) {
    try {
      var raw = sessionStorage.getItem(CACHE_PREFIX + variantId);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || Date.now() - parsed.t > CACHE_TTL_MS) return null;
      return parsed.locations;
    } catch (e) { return null; }
  }

  function writeCache(variantId, locations) {
    try {
      sessionStorage.setItem(CACHE_PREFIX + variantId, JSON.stringify({ t: Date.now(), locations: locations }));
    } catch (e) { /* quota or disabled storage — ignore */ }
  }

  function log(label, data) {
    if (!DEBUG) return;
    console.group('%c[IBL] ' + label, 'color: #0b6bcb; font-weight: bold');
    if (data !== undefined) {
      if (typeof data === 'object') {
        console.table ? console.table(data) : console.log(data);
      } else {
        console.log(data);
      }
    }
    console.groupEnd();
  }

  function isValidVariantId(id) {
    return /^\d+$/.test(String(id));
  }

  function fetchInventoryOnce(variantId) {
    var controller = window.AbortController ? new AbortController() : null;
    var timeout = setTimeout(function () {
      if (controller) controller.abort();
    }, REQUEST_TIMEOUT_MS);

    return fetch(API_BASE + variantId, controller ? { signal: controller.signal } : undefined)
      .then(function (response) {
        if (!response.ok) {
          log('fetchInventory — HTTP error', { variantId: variantId, status: response.status });
          return null;
        }
        return response.json();
      })
      .then(function (data) {
        if (!data || !data.locations) {
          log('fetchInventory — no locations in response', { variantId: variantId, data: data });
          return null;
        }
        log('fetchInventory — received ' + data.locations.length + ' locations', { variantId: variantId });
        return data.locations;
      })
      .catch(function (err) {
        log('fetchInventory — network error', { variantId: variantId, error: String(err) });
        return null;
      })
      .finally(function () {
        clearTimeout(timeout);
      });
  }

  function fetchInventory(variantId) {
    if (!isValidVariantId(variantId)) {
      log('fetchInventory — INVALID variant ID', variantId);
      return Promise.resolve(null);
    }

    var cached = readCache(variantId);
    if (cached) {
      log('fetchInventory — cache hit', { variantId: variantId, count: cached.length });
      return Promise.resolve(cached);
    }

    log('fetchInventory — requesting', { variantId: variantId, url: API_BASE + variantId });

    function attemptFetch(attempt) {
      return fetchInventoryOnce(variantId).then(function (locations) {
        if (locations) {
          writeCache(variantId, locations);
          return locations;
        }

        if (attempt >= MAX_REQUEST_ATTEMPTS) {
          log('fetchInventory — failed after all attempts', { variantId: variantId, attempts: attempt });
          return null;
        }

        log('fetchInventory — retrying after ' + RETRY_DELAY_MS + 'ms', {
          variantId: variantId,
          nextAttempt: attempt + 1,
          maxAttempts: MAX_REQUEST_ATTEMPTS
        });

        return new Promise(function (resolve) {
          setTimeout(function () {
            resolve(attemptFetch(attempt + 1));
          }, RETRY_DELAY_MS);
        });
      });
    }

    return attemptFetch(1).then(function (locations) {
      if (locations) return locations;
      return new Promise(function (resolve) {
        resolve(null);
      });
    });
  }

  function buildLocationItems(locations) {
    var sorted = locations.slice().sort(function (a, b) {
      var aDist = (a.distance != null) ? a.distance : Infinity;
      var bDist = (b.distance != null) ? b.distance : Infinity;
      if (aDist === bDist) return 0;
      return aDist - bDist;
    });

    if (DEBUG) {
      log('Locations (sorted by distance)', sorted.map(function (loc) {
        return {
          name: loc.name,
          distance: loc.distance != null ? loc.distance + ' km' : 'N/A',
          stock: loc.stock != null ? loc.stock : 0,
          enabled: loc.enabled,
          id: loc.id
        };
      }));
    }

    var items = [];
    var nearestStock = 0;
    for (var i = 0; i < sorted.length; i++) {
      var loc = sorted[i];
      if (!loc.enabled) continue;

      var stock = loc.stock ? loc.stock : 0;
      if (items.length === 0) nearestStock = stock;

      var li = document.createElement('li');

      var nameSpan = document.createElement('span');
      nameSpan.className = 'location';
      nameSpan.textContent = loc.name;
      li.appendChild(nameSpan);

      if (loc.distance != null) {
        var distSpan = document.createElement('span');
        distSpan.className = 'distance';
        distSpan.textContent = '(' + loc.distance + ' km)';
        li.appendChild(distSpan);
      }

      var sep = document.createTextNode(': ');
      li.appendChild(sep);

      var stockStrong = document.createElement('strong');
      stockStrong.className = 'stockCount';
      stockStrong.textContent = stock + ' in stock';
      li.appendChild(stockStrong);

      items.push(li);
    }
    return { items: items, nearestStock: nearestStock };
  }

  function findAtcButton(container) {
    // Scope to the Shopify section wrapper — prevents cross-contamination
    // when multiple product sections exist on the same page (e.g. homepage)
    var section = container.closest('.shopify-section');
    if (section) return section.querySelector('button[data-variant-atc]');
    return null;
  }

  function updateAtcButton(button, nearestStock, variant, state) {
    if (!button) return;

    var originalText = button.getAttribute('data-add-to-cart-text') || 'Add to Cart';
    var action;

    if (!variant || !variant.available) {
      action = 'UNCHANGED — Shopify variant not available, theme controls button';
    } else if (state === 'checking') {
      button.disabled = true;
      button.textContent = 'Checking stock...';
      action = 'DISABLED — checking GeoStock inventory';
    } else if (nearestStock === 0) {
      button.disabled = true;
      button.textContent = NO_LOCAL_STOCK_TEXT;
      action = 'DISABLED — nearest GeoStock location has no stock';
    } else {
      button.disabled = false;
      button.textContent = originalText;
      action = nearestStock == null ? 'ENABLED — GeoStock unavailable' : 'ENABLED — nearest GeoStock location has stock';
    }

    button.classList.add('ibl-ready');

    log('ATC button decision', {
      action: action,
      nearestStock: nearestStock,
      buttonDisabled: button.disabled,
      buttonText: button.textContent,
      variantId: variant ? variant.id : 'N/A',
      variantAvailable: variant ? variant.available : 'N/A'
    });
  }

  function populateBlock(block, variant) {
    var variantId = block.getAttribute('data-variant-id');
    if (!variantId) return;

    log('populateBlock — start', {
      variantId: variantId,
      trigger: variant ? 'variant (id: ' + variant.id + ', available: ' + variant.available + ')' : 'initial page load'
    });

    // Stamp a unique request ID so stale responses are discarded
    var reqId = (requestIds.get(block) || 0) + 1;
    requestIds.set(block, reqId);

    var loader = block.querySelector('.inventory-by-location-loader');
    var errorEl = block.querySelector('.inventory-by-location-error');
    var listEl = block.querySelector('ul[data-ibl-inventory]');
    var atcButton = findAtcButton(block);

    updateAtcButton(atcButton, null, variant, 'checking');

    if (loader) loader.style.display = '';
    if (errorEl) {
      errorEl.style.display = 'none';
      errorEl.textContent = '';
      errorEl.classList.remove('ibl-soft');
    }
    if (listEl) {
      listEl.style.display = 'none';
      while (listEl.firstChild) listEl.removeChild(listEl.firstChild);
    }

    fetchInventory(variantId).then(function (locations) {
      // Stale response — a newer request was made, discard this one
      if (requestIds.get(block) !== reqId) {
        log('populateBlock — STALE response discarded', { variantId: variantId, reqId: reqId });
        return;
      }

      try {
        if (loader) loader.style.display = 'none';

        if (!locations || locations.length === 0) {
          log('populateBlock — no locations returned, re-enabling button', { variantId: variantId });
          if (errorEl) {
            errorEl.textContent = SOFT_FALLBACK_TEXT;
            errorEl.classList.add('ibl-soft');
            errorEl.style.display = '';
          }
          updateAtcButton(atcButton, null, variant, 'fallback');
          return;
        }

        var result = buildLocationItems(locations);
        if (result.items.length === 0) {
          log('populateBlock — no enabled locations returned, re-enabling button', { variantId: variantId });
          if (errorEl) {
            errorEl.textContent = SOFT_FALLBACK_TEXT;
            errorEl.classList.add('ibl-soft');
            errorEl.style.display = '';
          }
          updateAtcButton(atcButton, null, variant, 'fallback');
          return;
        }

        if (listEl) {
          for (var i = 0; i < result.items.length; i++) {
            listEl.appendChild(result.items[i]);
          }
          listEl.style.display = '';
        }

        log('Nearest location result', {
          variantId: variantId,
          nearestStock: result.nearestStock,
          totalLocations: result.items.length
        });

        updateAtcButton(atcButton, result.nearestStock, variant, 'loaded');
      } catch (e) {
        log('populateBlock — ERROR in callback', { variantId: variantId, error: String(e) });
        if (errorEl) {
          errorEl.textContent = SOFT_FALLBACK_TEXT;
          errorEl.classList.add('ibl-soft');
          errorEl.style.display = '';
        }
        updateAtcButton(atcButton, null, variant, 'fallback');
      }
    });
  }

  function initAll() {
    var blocks = document.querySelectorAll('[data-ibl-product-block]');
    log('initAll — found ' + blocks.length + ' inventory block(s)');
    for (var i = 0; i < blocks.length; i++) {
      var block = blocks[i];
      // Build initial variant from data attributes — avoids iblPending complexity
      var initialVariant = {
        id: block.getAttribute('data-variant-id'),
        available: block.getAttribute('data-variant-available') !== 'false'
      };
      populateBlock(block, initialVariant);
    }
  }

  function onVariantChange(event) {
    try {
      var variant = event.detail && event.detail.variant;
      if (!variant || !variant.id) return;

      // Scope to the section that fired the event — prevents cross-contamination
      // when multiple product sections exist on the same page (e.g. homepage)
      var scope = event.target ? (event.target.closest('.shopify-section') || document) : document;

      log('on:variant:change', {
        variantId: variant.id,
        available: variant.available,
        title: variant.title || variant.name || 'N/A',
        scope: scope === document ? 'document (global)' : scope.id || 'section'
      });

      var blocks = scope.querySelectorAll('[data-ibl-product-block]');
      for (var i = 0; i < blocks.length; i++) {
        blocks[i].setAttribute('data-variant-id', variant.id);
        blocks[i].setAttribute('data-variant-available', String(variant.available));
        populateBlock(blocks[i], variant);
      }

      var buttons = scope.querySelectorAll('button[data-variant-atc]');
      for (var j = 0; j < buttons.length; j++) {
        buttons[j].setAttribute('data-variant-atc', variant.id);
      }
    } catch (e) {
      // Silently handle errors
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('on:variant:change', onVariantChange);

  if (DEBUG) {
    log('inventory-by-location-main.js loaded', {
      hint: 'Add ?ibl-debug=true to URL to see these logs',
      apiBase: API_BASE
    });
  }
})();
